// Imports
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import type { RequestError } from '@/renderer/utils/errors';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

// Types
type ForgotPasswordData = { email: string };

// API requests
const passwordLinkRequest: RequestSchema<API.ResetState, { email: string }> = {
  name: 'passwordLinkRequest',
  input: '/api/v1/auth/reset2',
  method: 'POST',
};

export default function RequestReset() {
  // Form hooks
  const form = useForm<WithFormErrors<ForgotPasswordData>>();

  // Request hooks
  const request = useRequest(passwordLinkRequest, { manual: true });

  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        try {
          const result = await request.run(body, { throwOnRun: true });

          if (result?.message === 'API.AUTHENTICATION.RESET.SUCCESS') {
            setIsSubmitted(true);
          }
        } catch (error) {
          if ((error as RequestError).data?.code === 404) {
            form.setError('email', {
              type: 'manual',
              message: 'The email is not a valid user account',
            });
          }
        }
      },
    },
    [],
  );

  return (
    <>
      <h1 className="text-center text-2xl font-bold text-zinc-900 dark:text-zinc-200">
        Reset your password
      </h1>

      {isSubmitted ? (
        <>
          <p className="text-center text-sm">Password reset request has been emailed to you.</p>
          <div className="flex justify-center text-zinc-700 dark:text-zinc-300">
            <a href="/signin" className="flex items-center space-x-1 text-sm font-semibold">
              <ArrowLeftIcon height={16} className="h-4 w-4" />
              <span>Back to sign in </span>
            </a>
          </div>
        </>
      ) : (
        <>
          <p className="text-center text-sm">
            Enter your email and we'll send you a link to reset your password.
          </p>
          <form onSubmit={handleFormSubmit} className="space-y-6">
            <FormControl
              form={form}
              name="email"
              label="Primary email address"
              register={{
                required: {
                  value: true,
                  message: 'Email is required',
                },
                pattern: {
                  value: EMAIL_REGEX,
                  message: 'Email is not a valid email!',
                },
              }}
              autoCapitalize="off"
              autoCorrect="off"
              autoSave="off"
            />

            <FormErrors
              form={form}
              request={request}
              rewrite={(err) => {
                return err.code === 404 ? '' : err.toString();
              }}
            />

            <FormSubmitButton disabled={request.loading} loading={request.loading}>
              Reset your password
            </FormSubmitButton>
            <div className="flex items-center justify-center space-x-2 text-zinc-700 dark:text-zinc-300">
              <span className="text-sm">Wrong place?</span>
              <a href="/signin" className="flex items-center space-x-1 text-sm font-semibold">
                <span>Back to sign in </span>
                <ArrowRightIcon height={16} className="h-4 w-4" />
              </a>
            </div>
          </form>
        </>
      )}
    </>
  );
}
